import { DateTime } from "luxon";

import { IPlannerPartAccount, IPlannerVehicleRow } from "../models/ProductionPlanner";
import formatDateTime from "./formatDateTime";
import { complexityLabel } from "./vehicleDisplay";

// "ELV Number" is the vehicle's stock number - the name the line sheet has always used for it.
export const PLANNER_LINE_EXPORT_HEADERS = ["ELV Number", "VRM", "Model", "Complexity"];

// The account columns, in the order the sheet shows them. Any other category is left off.
export const PLANNER_LINE_EXPORT_ACCOUNT_COLUMNS = ["Engine", "ECU", "ABS", "Bumper"];

// A part category lands in a column when its name contains the column's word ("Front
// Bumper", "Engine ECU"). Engine is tried last so "Engine ECU" counts as an ECU.
const COLUMN_MATCHERS: [string, RegExp][] = [
  ["ECU", /\becus?\b/i],
  ["ABS", /\babs\b/i],
  ["Bumper", /\bbumpers?\b/i],
  ["Engine", /\bengines?\b/i],
];

export function plannerLineExportColumn(categoryName: string): string | null {
  return COLUMN_MATCHERS.find(([, pattern]) => pattern.test(categoryName))?.[0] ?? null;
}

// Sits straight after the Engine column. NG is the same rule as the "Engines Flagged NG at
// Triage" report - the ENGINE GOOD triage step answered No - and anything else reads OK.
export const PLANNER_LINE_EXPORT_ENGINE_CONDITION = "Engine Condition";

const withEngineCondition = (cells: string[], condition: string): string[] => {
  const at = PLANNER_LINE_EXPORT_ACCOUNT_COLUMNS.indexOf("Engine") + 1;
  return [...cells.slice(0, at), condition, ...cells.slice(at)];
};

// Rows for a single dismantling line's day, ready for Papa.unparse: a title row, the
// headers, then one row per vehicle in the order given. Pass the vehicles already sorted
// as the planner shows them - the sheet is worked top to bottom.
export function plannerLineExportRows(
  lineName: string,
  date: string,
  vehicles: IPlannerVehicleRow[],
  partAccounts: IPlannerPartAccount[],
  engineNotGoodIds: Set<string> = new Set()
): string[][] {
  const accountsByVehicle = new Map<string, Map<string, string[]>>();
  partAccounts.forEach((a) => {
    const column = plannerLineExportColumn(a.categoryName);
    if (!column) return;

    const byColumn = accountsByVehicle.get(a.vehicleId) ?? new Map<string, string[]>();
    const names = byColumn.get(column) ?? [];
    if (!names.includes(a.accountName)) names.push(a.accountName);
    byColumn.set(column, names);
    accountsByVehicle.set(a.vehicleId, byColumn);
  });

  const title = `${lineName} ${formatDateTime(date, DateTime.DATE_HUGE)}`;

  return [
    [title],
    [
      ...PLANNER_LINE_EXPORT_HEADERS,
      ...withEngineCondition(PLANNER_LINE_EXPORT_ACCOUNT_COLUMNS, PLANNER_LINE_EXPORT_ENGINE_CONDITION),
    ],
    ...vehicles.map((v) => {
      const byColumn = accountsByVehicle.get(v.id);
      return [
        v.stockNumber ?? "",
        v.vrm ?? "",
        [v.combinedMake, v.combinedModel].filter(Boolean).join(" "),
        complexityLabel(v.complexity),
        ...withEngineCondition(
          PLANNER_LINE_EXPORT_ACCOUNT_COLUMNS.map((c) => byColumn?.get(c)?.join(", ") ?? ""),
          engineNotGoodIds.has(v.id) ? "NG" : "OK"
        ),
      ];
    }),
  ];
}

export function plannerLineExportFileName(lineName: string, date: string): string {
  const day = DateTime.fromISO(date).toFormat("yyyy-MM-dd");
  return `${lineName.replace(/[\\/:*?"<>|]/g, "-")} ${day}.csv`;
}
